import { useState } from 'react';
import type { SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { PageShell } from '../components/PageShell.tsx';
import { LoadingDots } from '../components/LoadingDots.tsx';
import { useFTPTest } from '../hooks/useFTPTest.ts';

export function FTPTest() {
  const { t } = useTranslation();
  const [power, setPower] = useState('');
  const mutation = useFTPTest();

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    const val = Number.parseInt(power, 10);
    if (val > 0) mutation.mutate(val);
  };

  return (
    <PageShell title={t('ftp.title')} subtitle={t('ftp.subtitle')}>
      {/* Explanation */}
      <div className="bg-(--color-bg-secondary) border border-(--color-border) rounded-xl p-5 mb-6">
        <h3 className="font-semibold text-(--color-accent) mb-2">{t('ftp.whatIsFTP')}</h3>
        <p className="text-sm text-(--color-text-secondary) leading-relaxed">
          {t('ftp.ftpExplain')}
        </p>
        <h3 className="font-semibold text-(--color-accent) mt-4 mb-2">{t('ftp.testingTips')}</h3>
        <ul className="text-sm text-(--color-text-secondary) space-y-1 list-disc list-inside">
          <li>{t('ftp.tip1')}</li>
          <li>{t('ftp.tip2')}</li>
          <li>{t('ftp.tip3')}</li>
          <li>{t('ftp.tip4')}</li>
          <li>{t('ftp.tip5')}</li>
        </ul>
      </div>

      <form onSubmit={handleSubmit} className="flex gap-3 mb-8">
        <input
          type="number"
          value={power}
          onChange={(e) => setPower(e.target.value)}
          placeholder={t('ftp.placeholder')}
          min={1}
          className="flex-1 bg-(--color-bg-tertiary) text-(--color-text-primary) rounded-lg px-4 py-3 text-sm border border-(--color-border) focus:outline-none focus:border-(--color-accent)"
        />
        <button
          type="submit"
          disabled={mutation.isPending}
          className="bg-green-400 text-gray-900 px-6 py-3 rounded-lg font-semibold text-sm hover:bg-green-300 disabled:opacity-40 transition-colors"
        >
          {t('ftp.calculate')}
        </button>
      </form>

      {mutation.isPending && <LoadingDots text={t('ftp.calculating')} />}

      {mutation.data && (
        <div className="bg-(--color-bg-secondary) border border-(--color-border) rounded-xl p-6 text-center">
          <span className="text-sm text-(--color-text-secondary)">{t('ftp.estimatedFTP')}</span>
          <div className="text-4xl font-bold text-(--color-accent) my-2">{mutation.data.ftp}W</div>
          <p className="text-sm text-(--color-text-secondary)">
            {t('ftp.basedOn', { power: mutation.data.avg_power_20min })}
          </p>
          <p className="text-xs text-(--color-text-secondary) mt-2 italic">{mutation.data.note}</p>
        </div>
      )}
    </PageShell>
  );
}
